'use client';

import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { Portal } from '@ark-ui/react';
import { Box } from '@terra/ui-styled-system';
import {
  Menu,
  toasterApi,
  formatCategoryForDisplay,
} from '@terra/ui-design-system';
import {
  getSignalCategoriesForTargetType,
  type SignalCategoryType,
  type TargetTypeType,
} from '@terra/shared-schemas-orchestrator';
import { terraProxyApiClient } from '../../clients/testerClient';
import { useTargetType } from '../../store/projectStore';
import { ListPageTablePill } from '../common/ListPageTablePill';

type SignalCategorySelectorProps = {
  signalId: string;
  currentCategory: SignalCategoryType;
  onCategoryChange?: (category: SignalCategoryType) => void;
  targetType?: TargetTypeType;
  disabled?: boolean;
};

export function SignalCategorySelector({
  signalId,
  currentCategory,
  onCategoryChange,
  targetType: targetTypeProp,
  disabled = false,
}: SignalCategorySelectorProps) {
  const t = useTranslations('signals.signalCategorySelector');
  const storeTargetType = useTargetType();
  const targetType = targetTypeProp ?? storeTargetType ?? 'WEB_APPLICATION';
  const categoryOptions = useMemo(() => {
    const categories = [...getSignalCategoriesForTargetType(targetType)];

    if (!categories.includes(currentCategory)) {
      categories.push(currentCategory);
    }

    return categories;
  }, [currentCategory, targetType]);

  const handleCategoryChange = async (
    newCategory: SignalCategoryType,
  ): Promise<void> => {
    if (disabled || newCategory === currentCategory) {
      return;
    }

    try {
      onCategoryChange?.(newCategory);

      await terraProxyApiClient.updateSignal(signalId, {
        category: newCategory,
      });
    } catch (error) {
      onCategoryChange?.(currentCategory);

      console.error('Failed to update signal category:', error);
      toasterApi.error({
        title: 'Failed to update category',
        description: 'There was an error updating the category.',
      });
    }
  };

  const createClickHandler = (value: SignalCategoryType) => () =>
    handleCategoryChange(value);

  return (
    <Menu.Root
      lazyMount
      unmountOnExit
      positioning={{
        placement: 'bottom-start',
        strategy: 'fixed',
      }}
    >
      <Menu.Trigger asChild>
        <Box
          display="inline-flex"
          maxW="full"
          cursor={disabled ? 'not-allowed' : 'pointer'}
          pointerEvents={disabled ? 'none' : undefined}
          _hover={disabled ? undefined : { opacity: 0.8 }}
          title="Change category"
        >
          <ListPageTablePill>
            {formatCategoryForDisplay(currentCategory)}
          </ListPageTablePill>
        </Box>
      </Menu.Trigger>
      <Portal>
        <Menu.Positioner>
          <Menu.Content maxH="300px" overflowY="auto">
            <Menu.ItemGroup>
              <Menu.ItemGroupLabel fontSize="xs">
                {t('changeCategoryTo')}
              </Menu.ItemGroupLabel>
              <Menu.Separator />
              {categoryOptions.map((category) => (
                <Menu.Item
                  key={category}
                  value={category}
                  disabled={disabled}
                  onClick={createClickHandler(category)}
                  fontWeight={
                    currentCategory === category ? 'semibold' : 'normal'
                  }
                >
                  {formatCategoryForDisplay(category)}
                </Menu.Item>
              ))}
            </Menu.ItemGroup>
          </Menu.Content>
        </Menu.Positioner>
      </Portal>
    </Menu.Root>
  );
}
